import { catchError, defer, from, Observable, ObservableInput, tap, throwError } from 'rxjs'
import { auth0Config, signup, SignUpOptions } from '../../auth'
import { setHubSpotTrackingOperation } from '../../hubspot-tracking/state'
import { DeferAfterTimeoutError, parseRegistrationError } from '../errors'
import { setRegistrationState } from '../state'
import { RegistrationData, RegistrationResponse, RegistrationUserMetadata } from '../types'

const { connection, scope } = auth0Config

type OmitRegData<TData extends RegistrationData> = Omit<TData, keyof RegistrationData>

const signUpOptionsFromInput = <TData extends RegistrationData>({
  firstName,
  lastName,
  email,
  password,
  ...userMetadata
}: TData): SignUpOptions<OmitRegData<TData>> => {
  return {
    email,
    password,
    connection,
    scope,
    given_name: firstName,
    family_name: lastName,
    user_metadata: userMetadata,
  }
}

const newRegistrationRequest = <TMetadata extends RegistrationUserMetadata>(
  options: SignUpOptions<TMetadata>
): Observable<RegistrationResponse<TMetadata>> => defer(() => from(signup(options)))

const continueRegistrationRequest = <TMetadata extends RegistrationUserMetadata>(
  options: SignUpOptions<TMetadata>,
  cursor: string
): Observable<RegistrationResponse<TMetadata>> =>
  defer(() =>
    from(
      signup({
        ...options,
        user_metadata: {
          ...options.user_metadata,
          deferred_cursor: cursor,
        },
      })
    )
  )

export const registrationRequest = <TInput extends RegistrationData>({
  input,
  isGuestCheckoutEnabled,
}: {
  input: TInput
  isGuestCheckoutEnabled?: boolean
}): Observable<RegistrationResponse<OmitRegData<TInput>>> => {
  const signUpOptions = signUpOptionsFromInput(input)
  let continueCount = 0
  setRegistrationState({ name: 'signup_loading', continueCount })

  // recursive error handler
  const continueIfDeferred: (
    err: unknown,
    caught: ObservableInput<RegistrationResponse<OmitRegData<TInput>>>
  ) => Observable<RegistrationResponse<OmitRegData<TInput>>> = (errorRaw) => {
    const error = parseRegistrationError(errorRaw, isGuestCheckoutEnabled ?? false)
    if (error instanceof DeferAfterTimeoutError) {
      continueCount += 1
      setRegistrationState({ name: 'signup_loading', continueCount })
      return continueRegistrationRequest(signUpOptions, error.cursor).pipe(
        catchError(continueIfDeferred)
      )
    }
    setRegistrationState({ name: 'signup_error', error })
    return throwError(() => error)
  }

  return newRegistrationRequest(signUpOptions).pipe(
    catchError(continueIfDeferred),
    tap((response) => {
      setRegistrationState({ name: 'signup_complete', response })
      setHubSpotTrackingOperation({ key: 'trackEvent', params: [{ id: 'create-account' }] })
    })
  )
}
